import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { createVirtualAgentConfirmationModalMutation } from '@atlassian/jira-relay/src/__generated__/createVirtualAgentConfirmationModalMutation.graphql';
import { usePathParam } from '@atlassian/react-resource-router';
import messages from './messages';

export const useCreateVirtualAgentForProject = (
	jiraProjectId: string,
): [
	{ isLoading: boolean },
	{ createVirtualAgentForProject: (defaultRequestTypeId: string) => void },
] => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [projectKey] = usePathParam('projectKey');
	const { showFlag } = useFlagService();

	const onError = useCallback(
		(error: Error) => {
			showFlag({
				type: 'error',
				title: fg('virtual_agent_rebrand_to_virtual_service_agent')
					? formatMessage(messages.errorFlagFailToCreateVsa)
					: formatMessage(messages.errorFlagFailToCreateVa),
				description: formatMessage(messages.errorFlagCtaRefreshAndTryAgain),
			});
			// https://data-portal.internal.atlassian.com/analytics/registry/61111
			fireErrorAnalytics({
				error,
				event: createAnalyticsEvent({}),
				attributes: {
					cause: 'DATA_ERROR',
				},
				meta: {
					id: 'createVirtualAgent',
					teamName: 'jsd-flux-capacitors',
					packageName: 'jiraServicedeskSettingsVirtualAgentLanding',
				},
			});
		},
		[createAnalyticsEvent, showFlag, formatMessage],
	);

	const [createVirtualAgentMutation, isLoading] =
		useMutation<createVirtualAgentConfirmationModalMutation>(graphql`
			mutation createVirtualAgentConfirmationModalMutation(
				$jiraProjectId: ID!
				$defaultRequestTypeId: String
			) {
				virtualAgent {
					createVirtualAgentConfiguration(
						input: { defaultJiraRequestTypeId: $defaultRequestTypeId }
						jiraProjectId: $jiraProjectId
					) {
						success
						virtualAgentConfiguration {
							__id
							id
							defaultJiraRequestTypeId
						}
						errors {
							message
						}
					}
				}
			}
		`);

	const createVirtualAgentForProject = useCallback(
		(defaultRequestTypeId: string) => {
			createVirtualAgentMutation({
				variables: {
					jiraProjectId,
					defaultRequestTypeId,
				},
				onCompleted: (response) => {
					if (
						response.virtualAgent?.createVirtualAgentConfiguration?.virtualAgentConfiguration !=
						null
					) {
						const virtualAgentConfigurationId =
							response.virtualAgent?.createVirtualAgentConfiguration?.virtualAgentConfiguration?.id;
						// https://data-portal.internal.atlassian.com/analytics/registry/54868
						fireTrackAnalytics(
							createAnalyticsEvent({}),
							'virtualAgent created',
							virtualAgentConfigurationId ?? 'UNKNOWN',
						);

						// Redirect to intents page
						window.location.href = `/jira/servicedesk/projects/${projectKey}/settings/virtual-agent/intents`;
					} else {
						const error = new Error(
							response.virtualAgent?.createVirtualAgentConfiguration?.errors?.[0].message ??
								'Unknown error while creating virtual service agent',
						);
						onError(error);
					}
				},
				onError: (error) => {
					onError(error);
				},
			});
		},
		[createVirtualAgentMutation, jiraProjectId, createAnalyticsEvent, onError, projectKey],
	);

	return [{ isLoading }, { createVirtualAgentForProject }];
};
